import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ClientLayout from '../../Components/Layout/ClientLayout/ClientLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';

const API = process.env.REACT_APP_API_URL;

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDateLong(iso) {
  return new Date(iso).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

// Generate YYYY-MM-DD strings for the next N days in local time
function generateDates(count) {
  const dates = [];
  for (let i = 0; i < count; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${day}`);
  }
  return dates;
}

const selectClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const ClientSchedule = () => {
  const { tenantDomain } = useParams();
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const dates = useMemo(() => generateDates(14), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slots, setSlots] = useState([]);
  const [myPackages, setMyPackages] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [selectedLocationId, setSelectedLocationId] = useState('all');

  const [confirmSlot, setConfirmSlot] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tenantDomain]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [slotsRes, pkgRes] = await Promise.all([
        fetch(`${API}/${tenantDomain}/client-schedule?tz=${encodeURIComponent(tz)}`, { credentials: 'include' }),
        fetch(`${API}/${tenantDomain}/my-packages`, { credentials: 'include' }),
      ]);
      if (slotsRes.ok) {
        const data = await slotsRes.json();
        setSlots(data.slots);
      } else {
        setError('Failed to load available times.');
      }
      if (pkgRes.ok) setMyPackages(await pkgRes.json());
    } catch {
      setError('Failed to load schedule.');
    } finally {
      setLoading(false);
    }
  };

  const locations = useMemo(() => {
    const seen = new Map();
    slots.forEach(s => { if (!seen.has(s.locationId)) seen.set(s.locationId, s.locationName); });
    return Array.from(seen.entries()).map(([locationId, locationName]) => ({ locationId, locationName }));
  }, [slots]);

  const visibleSlots = useMemo(() =>
    slots.filter(s =>
      s.date === selectedDate &&
      (selectedLocationId === 'all' || s.locationId === selectedLocationId)
    ),
    [slots, selectedDate, selectedLocationId]
  );

  const handleSlotTap = (slot) => {
    setConfirmSlot(slot);
    setBookingError(null);
    setSelectedPackageId(myPackages.length > 0 ? myPackages[0].clientPackageId : '');
  };

  const handleConfirmBooking = async () => {
    if (!confirmSlot || !selectedPackageId) return;
    setBooking(true);
    setBookingError(null);
    try {
      const body = {
        locationId: confirmSlot.locationId,
        clientPackageId: selectedPackageId,
        scheduledAt: confirmSlot.scheduledAt,
        durationMins: confirmSlot.durationMins,
      };
      if (confirmSlot.joinable) body.bookingId = confirmSlot.bookingId;

      const res = await fetch(`${API}/${tenantDomain}/client-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.status === 409) {
        const text = await res.text();
        if (text === 'InsufficientCredits') setBookingError('No credits remaining on this package.');
        else if (text === 'BookingFull') setBookingError('This slot just filled up — please choose another time.');
        else if (text === 'DuoPackageCannotJoinSoloSlot') setBookingError('Your duo package cannot be used for a solo session.');
        else if (text.startsWith('OutsideAvailability')) setBookingError('This time is no longer available.');
        else setBookingError('Could not complete booking. Please try again.');
        return;
      }
      if (!res.ok) throw new Error();

      setConfirmSlot(null);
      await fetchData();
    } catch {
      setBookingError('Something went wrong. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  return (
    <ClientLayout>
      {/* Location filter — only shown when multiple locations have availability */}
      {locations.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-3 border-b overflow-x-auto shrink-0">
          <button
            onClick={() => setSelectedLocationId('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${
              selectedLocationId === 'all'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'text-muted-foreground border-input'
            }`}
          >
            All
          </button>
          {locations.map(loc => (
            <button
              key={loc.locationId}
              onClick={() => setSelectedLocationId(loc.locationId)}
              className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${
                selectedLocationId === loc.locationId
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'text-muted-foreground border-input'
              }`}
            >
              {loc.locationName}
            </button>
          ))}
        </div>
      )}

      {/* Date strip */}
      <div className="flex gap-1 px-3 py-3 border-b overflow-x-auto shrink-0">
        {dates.map((date, i) => {
          const d = new Date(date + 'T12:00:00');
          const isSelected = date === selectedDate;
          return (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              className={`flex flex-col items-center justify-center min-w-[48px] h-[60px] rounded-xl text-xs font-medium transition-colors shrink-0 ${
                isSelected
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              <span className={`text-[10px] uppercase tracking-wide ${isSelected ? 'opacity-80' : ''}`}>
                {i === 0 ? 'Today' : DAY_LABELS[d.getDay()]}
              </span>
              <span className={`text-lg font-semibold leading-tight ${isSelected ? '' : 'text-foreground'}`}>
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Slot list */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-10">Loading...</p>
        ) : error ? (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
        ) : myPackages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm font-medium">No active packages</p>
            <p className="text-xs text-muted-foreground mt-1">Contact your trainer to get started.</p>
          </div>
        ) : visibleSlots.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No available times on this day.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {visibleSlots.map((slot, i) => (
              <div key={i} className="rounded-xl border bg-card px-4 py-4 flex items-center justify-between gap-3">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-base font-semibold">{formatTime(slot.scheduledAt)}</span>
                  <span className="text-sm text-muted-foreground">
                    {slot.durationMins} min · {slot.locationName}
                  </span>
                  {slot.joinable && (
                    <span className="text-xs font-medium text-green-600 mt-0.5">
                      {slot.spotsRemaining === 1 ? '1 spot available' : `${slot.spotsRemaining} spots available`}
                    </span>
                  )}
                </div>
                <Button size="sm" onClick={() => handleSlotTap(slot)} className="shrink-0">
                  {slot.joinable ? 'Join' : 'Book'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmSlot} onOpenChange={(open) => { if (!open) setConfirmSlot(null); }}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle>{confirmSlot?.joinable ? 'Join Session' : 'Book Session'}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {confirmSlot && (
              <div className="flex flex-col gap-4">
                {bookingError && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2.5 text-sm text-destructive">{bookingError}</div>
                )}
                <div className="rounded-lg border bg-muted/30 px-4 py-3 flex flex-col gap-1">
                  <span className="font-semibold">{formatTime(confirmSlot.scheduledAt)}</span>
                  <span className="text-sm text-muted-foreground">{formatDateLong(confirmSlot.scheduledAt)}</span>
                  <span className="text-sm text-muted-foreground">{confirmSlot.durationMins} min · {confirmSlot.locationName}</span>
                  {confirmSlot.joinable && (
                    <span className="text-xs text-green-600 font-medium mt-0.5">Joining an existing session</span>
                  )}
                </div>

                {myPackages.length > 1 ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium">Package</label>
                    <select
                      value={selectedPackageId}
                      onChange={e => setSelectedPackageId(e.target.value)}
                      className={selectClass}
                    >
                      {myPackages.map(pkg => (
                        <option key={pkg.clientPackageId} value={pkg.clientPackageId}>
                          {pkg.packageName} — {pkg.creditsRemaining} credit{pkg.creditsRemaining !== 1 ? 's' : ''} left
                        </option>
                      ))}
                    </select>
                  </div>
                ) : myPackages.length === 1 ? (
                  <p className="text-sm text-muted-foreground">
                    Using: {myPackages[0].packageName} · {myPackages[0].creditsRemaining} credit{myPackages[0].creditsRemaining !== 1 ? 's' : ''} remaining
                  </p>
                ) : null}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSlot(null)} disabled={booking}>Cancel</Button>
            <Button onClick={handleConfirmBooking} disabled={booking || !selectedPackageId}>
              {booking ? 'Booking...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
};

export default ClientSchedule;
